import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"
import { BACKEND_URL } from "../config";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function quoteNickname(nickname) {
  return nickname ? `\u201C${nickname}\u201D` : "";
}


/**
 * Get the full URL for an uploaded file.
 * Handles both Cloudinary URLs (https://...) and legacy local paths (filename only)
 * 
 * @param {string} filename - The filename or full URL
 * @param {string} fallback - Optional fallback if filename is empty
 * @returns {string} Full URL to the file
 */
export function getUploadUrl(filename, fallback = null) {
  if (!filename) return fallback;
  
  // Already a full URL (Cloudinary or other CDN)
  if (filename.startsWith('http://') || filename.startsWith('https://')) {
    return filename;
  }
  
  // Already has /api prefix
  if (filename.startsWith('/api/')) {
    return `${BACKEND_URL}${filename}`;
  }
  
  // Legacy local file - prepend API URL
  return `${BACKEND_URL}/api/uploads/${filename}`;
}

/**
 * Apply a "Meu Canto" custom background only if the image actually loads.
 * Preloads the image; on success calls setter(url), otherwise leaves the
 * current value untouched so the page falls back to the standard hero
 * instead of rendering a flat dark background (e.g. when the file is
 * missing in the current environment).
 *
 * @param {string} filename - stored background filename or full URL
 * @param {(url: string) => void} setter - state setter for the background
 */
export function applyBackgroundIfLoads(filename, setter) {
  if (!filename) return;
  const url = getUploadUrl(filename);
  if (!url) return;
  const img = new Image();
  img.onload = () => setter(url);
  img.src = url;
}

/**
 * Get a thumbnail URL for a Cloudinary image
 * 
 * @param {string} url - Original Cloudinary URL
 * @param {number} width - Desired width
 * @param {number} height - Optional height
 * @param {string} mode - Crop mode: 'fill' (crop to fit), 'fit' (scale to fit, no crop), 'limit' (scale down only)
 * @returns {string} Transformed URL or original if not Cloudinary
 */
export function getCloudinaryThumbnail(url, width = 300, height = null, mode = 'fill') {
  if (!url || !url.includes('cloudinary.com')) {
    return url;
  }
  
  let transform = `w_${width}`;
  if (height) transform += `,h_${height}`;
  transform += `,c_${mode},q_auto,f_auto`;
  
  return url.replace('/upload/', `/upload/${transform}/`);
}
